import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmAction,
  DetailPage,
  EmptyState,
  Input,
  Skeleton,
  StatusBadge,
} from '@skylabs-digital/backoffice-kit';
import { useApiClient, useSnackbar } from '@skylabs-digital/react-proto-kit';
import { Pencil, RotateCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { appsApi } from '../api/apps.api';
import { unwrap } from '../api/unwrap';
import { ApiKeyReveal } from '../components/apps/ApiKeyReveal';
import { AppChannelConfigSection } from '../components/apps/AppChannelConfigSection';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4 border-b border-border py-3 text-sm last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="col-span-2 text-foreground">{children}</dd>
    </div>
  );
}

export function AppDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { connector } = useApiClient();
  const { showSnackbar } = useSnackbar();
  const { data: app, loading, error, refetch } = appsApi.useById(id ?? '');
  const { mutate: updateApp, loading: updating } = appsApi.useUpdate();
  const { mutate: deleteApp } = appsApi.useDelete();

  const [rotatedKey, setRotatedKey] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');

  if (loading) {
    return (
      <DetailPage title="App" breadcrumbs={[{ label: 'Apps', to: '/apps' }, { label: '…' }]}>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </DetailPage>
    );
  }

  if (error || !app || !id) {
    return (
      <DetailPage title="App" breadcrumbs={[{ label: 'Apps', to: '/apps' }]}>
        <EmptyState
          title="App not found"
          description={error instanceof Error ? error.message : 'This app may have been deleted.'}
          action={<Button onClick={() => navigate('/apps')}>Back to apps</Button>}
          className="border-destructive/40 bg-destructive/5"
        />
      </DetailPage>
    );
  }

  const handleRotateKey = async () => {
    try {
      const result = await connector.post<{ apiKey: string }>(`apps/${id}/rotate-key`);
      setRotatedKey(unwrap(result).apiKey);
      showSnackbar({ message: 'API key rotated successfully', variant: 'success' });
    } catch {
      showSnackbar({ message: 'Failed to rotate key', variant: 'error' });
    }
  };

  const handleSaveName = async () => {
    try {
      await updateApp(id, { name: editName });
      showSnackbar({ message: 'App updated successfully', variant: 'success' });
      setEditing(false);
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to update app', variant: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteApp(id);
      showSnackbar({ message: 'Orphaned app deleted', variant: 'success' });
      navigate('/apps');
    } catch {
      showSnackbar({ message: 'Failed to delete app', variant: 'error' });
    }
  };

  return (
    <DetailPage
      title={app.name}
      description={
        <span className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={app.sandboxMode ? 'warning' : 'success'}>
            {app.sandboxMode ? 'Sandbox' : 'Production'}
          </StatusBadge>
          <StatusBadge tone={app.status === 'ORPHAN' ? 'danger' : 'success'}>
            {app.status === 'ORPHAN' ? 'Orphan' : 'Active'}
          </StatusBadge>
        </span>
      }
      breadcrumbs={[{ label: 'Apps', to: '/apps' }, { label: app.name }]}
      actions={
        <div className="flex items-center gap-2">
          {/* Preferencias de tenant y budgets se editan por app: desde aca ya
            va con el appId resuelto. */}
          <Button
            variant="outline"
            onClick={() => navigate(`/preferences?appId=${encodeURIComponent(app.id)}&tab=tenant`)}
          >
            Preferencias
          </Button>
          <Button variant="outline" onClick={() => navigate('/apps')}>
            Back
          </Button>
        </div>
      }
    >
      {rotatedKey ? (
        <ApiKeyReveal apiKey={rotatedKey} onDismiss={() => setRotatedKey(null)} />
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>App details</CardTitle>
            <CardDescription>Identity and sync information.</CardDescription>
          </div>
          {!editing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditName(app.name);
                setEditing(true);
              }}
            >
              <Pencil /> Edit
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          <dl>
            <Field label="ID">
              <span className="font-mono text-xs">{app.id}</span>
            </Field>
            <Field label="Name">
              {editing ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button size="sm" onClick={handleSaveName} disabled={!editName || updating}>
                    {updating ? 'Saving…' : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                app.name
              )}
            </Field>
            <Field label="External ID">
              <span className="font-mono text-xs">{app.externalId ?? '—'}</span>
            </Field>
            <Field label="Mode">{app.sandboxMode ? 'Sandbox' : 'Production'}</Field>
            <Field label="Last synced">
              {app.lastSyncedAt ? new Date(app.lastSyncedAt).toLocaleString() : '—'}
            </Field>
            <Field label="Created">{new Date(app.createdAt).toLocaleString()}</Field>
            <Field label="Updated">{new Date(app.updatedAt).toLocaleString()}</Field>
          </dl>
        </CardContent>
      </Card>

      {/* The section addresses channel config for THIS app; without the id a
          SUPERUSER read resolves to no app at all (noten-p9t). */}
      <AppChannelConfigSection appId={id} />

      <Card>
        <CardHeader>
          <CardTitle>API Key</CardTitle>
          <CardDescription>
            Rotate the API key for this app. The current key will be immediately invalidated.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ConfirmAction
            trigger={
              <Button>
                <RotateCw /> Rotate API key
              </Button>
            }
            title="Rotate API key?"
            description="This will invalidate the current API key. Services using it will stop working until updated with the new key."
            confirmLabel="Rotate key"
            destructive
            onConfirm={handleRotateKey}
          />
        </CardContent>
      </Card>

      {app.status === 'ORPHAN' ? (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Danger zone</CardTitle>
            <CardDescription>
              This app is orphaned (no longer exists in IdAchu). You can permanently delete it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ConfirmAction
              trigger={
                <Button variant="destructive">
                  <Trash2 /> Delete orphaned app
                </Button>
              }
              title="Delete this orphaned app?"
              description="This will permanently delete the app and revoke its API key. This action cannot be undone."
              confirmLabel="Delete app"
              destructive
              onConfirm={handleDelete}
            />
          </CardContent>
        </Card>
      ) : null}
    </DetailPage>
  );
}
